import React, { useState } from 'react'
import { Box, Container, Typography, Collapse, Card, CardContent } from '@mui/material'
import { useTheme } from '@mui/material/styles'

const FeaturedServices = () => {
  const theme = useTheme()
  const [expandedService, setExpandedService] = useState(null)

  const services = [
    {
      id: 'job-search',
      title: 'Job Search with Saved Filters and Alerts',
      description: 'Be notified whenever a new job fits your criteria. Filter by our extensive set of filters and get alerted so you never miss a job.',
      details: 'Create custom job alerts using our comprehensive filtering system including specialty, subspecialty, location, salary range, call schedule, visa sponsorship, and more. Receive instant notifications when new positions match your exact criteria, ensuring you\'re always first to know about opportunities that fit your career goals.'
    },
    {
      id: 'directory',
      title: 'Directory',
      description: 'Access our comprehensive directory of hospital recruiters and healthcare institutions. Connect directly with in-house recruiters and decision-makers at hospitals you\'re interested in.',
      details: 'Our directory includes verified contact information for in-house recruiters, hiring managers, and key personnel at hospitals and health systems nationwide. Skip the middleman and connect directly with the people who make hiring decisions.'
    },
    {
      id: 'salary',
      title: 'Salary Data',
      description: 'Explore our salary maps based on specialty and geography across the US. Understand average compensation for your specialty in different regions of the country.',
      details: 'Interactive salary maps showing compensation averages by medical specialty and geographic location throughout the United States. Compare salaries across states, regions, and metropolitan areas to make informed career decisions about where to practice.'
    },
    {
      id: 'application-tracking',
      title: 'Job Application Tracking',
      description: 'Keep track of your job applications in one organized place. No more scraps of paper or scattered Google sheets—manage your entire job search process.',
      details: 'Organize and track all your job applications and offer details in a centralized dashboard. While updates aren\'t automatic, you can manually track application status, contact information, and notes for each position you\'ve applied to.'
    },
    {
      id: 'housing',
      title: 'Housing Resources',
      description: 'Connect with top real estate agents in your area and receive exclusive rebates as a clinician. Simplify your home buying or renting process with trusted professionals.',
      details: 'Get connected with vetted, top-performing real estate agents who understand the unique needs of healthcare professionals. As a clinician, you\'ll receive exclusive rebates on your real estate transactions, helping you save money while finding the perfect home near your new workplace.'
    },
  ]

  const handleServiceClick = (serviceId) => {
    setExpandedService(expandedService === serviceId ? null : serviceId)
  }

  return (
    <Box sx={{ py: 10, backgroundColor: theme.palette.background.default }}>
      <Container maxWidth="lg">
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant="overline"
            sx={{
              color: theme.palette.grey[900],
              fontWeight: 600,
              fontSize: '0.9rem',
              letterSpacing: '0.1em',
              mb: 2,
              display: 'block'
            }}
          >
            CLINICIAN FEATURED SERVICES
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 700,
              color: theme.palette.grey[900],
              fontSize: { xs: '2rem', md: '2.5rem' },
              lineHeight: 1.2
            }}
          >
            Everything You Need—All In One Place
          </Typography>
        </Box>

        {/* Services List */}
        <Box sx={{ maxWidth: '600px', mx: 'auto' }}>
          {services.map((service) => (
            <Box key={service.id} sx={{ mb: 1 }}>
              {/* Service Item */}
              <Box
                onClick={() => handleServiceClick(service.id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 2,
                  px: 0,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    opacity: 0.7
                  }
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 400,
                    color: theme.palette.grey[900],
                    fontSize: '1rem',
                    opacity: 0.8
                  }}
                >
                  {service.title}
                </Typography>
                
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: theme.palette.primary.main,
                    fontSize: '1.2rem',
                    fontWeight: 'bold'
                  }}
                >
                  {expandedService === service.id ? '−' : '+'}
                </Box>
              </Box>

              {/* Expandable Content */}
              <Collapse in={expandedService === service.id}>
                <Card
                  sx={{
                    mt: 1,
                    mb: 3,
                    backgroundColor: 'white',
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: '12px',
                    boxShadow: theme.palette.shadows.light
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Typography
                      variant="body1"
                      sx={{
                        color: theme.palette.grey[900],
                        fontSize: '1rem',
                        lineHeight: 1.6,
                        mb: 2
                      }}
                    >
                      {service.description}
                    </Typography>
                    
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.grey[900],
                        fontSize: '0.95rem',
                        lineHeight: 1.6,
                        opacity: 0.8
                      }}
                    >
                      {service.details}
                    </Typography>
                  </CardContent>
                </Card>
              </Collapse>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  )
}

export default FeaturedServices
